import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Card, Tag, Space, Typography, Tooltip, Button, Popconfirm, message, Input, Spin, Select, DatePicker, AutoComplete, Modal, Radio, Form, Checkbox, Grid, Badge } from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import { DeleteOutlined, SearchOutlined, LoadingOutlined, EyeOutlined, UserOutlined, PrinterOutlined, StopOutlined, ExclamationCircleOutlined, CloseOutlined, DollarOutlined, CalculatorOutlined, FilterOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';
import { ordersApi, deliveryPersonsApi, clientsApi } from '../../utils/api';
import dayjs from 'dayjs';
// Accent contextuel : noir dans l'espace privé, bleu en dehors.
import { PRIVATE_PRIMARY_COLOR as PRIMARY_COLOR } from '../../theme/privateTheme';
// Aligne le défilement des livreurs sur le comportement mobile de la fiche commande.
import { getDeliveryPersonSelectProps } from '../../utils/deliveryPersonSelect';
import { getDeviseActive } from '../../utils/devise';

const { Title, Text } = Typography;
const { Option } = Select;

const OrdersList = () => {
  // Mobile (< md) : liste de cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  // Bloc de filtres repliable (mobile/tablette)
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeliveryPersons, setLoadingDeliveryPersons] = useState(true);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [clientSearchOptions, setClientSearchOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  // Conserve le numéro saisi afin de filtrer la liste côté serveur, même au-delà de la page courante.
  const [selectedOrderNumber, setSelectedOrderNumber] = useState('');
  // Conserve le texte recherché dans les notes de livraison de toutes les commandes.
  const [deliveryNotesSearch, setDeliveryNotesSearch] = useState('');
  // Diffère la requête API pendant la saisie afin d'éviter un chargement à chaque caractère.
  const [debouncedDeliveryNotesSearch, setDebouncedDeliveryNotesSearch] = useState('');

  // Gère la sélection et le formulaire communs à l'annulation groupée des commandes.
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isBulkCancelModalVisible, setIsBulkCancelModalVisible] = useState(false);
  const [bulkCancellationLoading, setBulkCancellationLoading] = useState(false);
  const [bulkCancelForm] = Form.useForm();
  const [isBulkPaymentModalVisible, setIsBulkPaymentModalVisible] = useState(false);
  const [bulkPaymentLoading, setBulkPaymentLoading] = useState(false);
  const [bulkPaymentForm] = Form.useForm();
  // Centralise le formulaire et l'état de l'attribution d'un livreur pour toutes les commandes sélectionnées.
  const [isBulkAssignmentModalVisible, setIsBulkAssignmentModalVisible] = useState(false);
  const [bulkAssignmentLoading, setBulkAssignmentLoading] = useState(false);
  const [bulkAssignmentForm] = Form.useForm();
  // Garde la calculatrice entièrement locale afin qu'aucun calcul ne modifie les commandes sélectionnées.
  const [isCalculatorVisible, setIsCalculatorVisible] = useState(false);
  const [calculatorExpression, setCalculatorExpression] = useState('');
  const [calculatorResult, setCalculatorResult] = useState(null);
  // Gère le choix et l’enregistrement de la nouvelle date pour le report groupé des livraisons.
  const [isBulkRescheduleModalVisible, setIsBulkRescheduleModalVisible] = useState(false);
  const [bulkRescheduleLoading, setBulkRescheduleLoading] = useState(false);
  const [bulkRescheduleForm] = Form.useForm();
  // Identifie la note en cours de sauvegarde pour éviter les enregistrements simultanés sur une même commande.
  const [savingDeliveryNotesOrderId, setSavingDeliveryNotesOrderId] = useState(null);

  // États pour le filtre par variante (depuis ProductList)
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedVariantName, setSelectedVariantName] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');

  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState(null);

  // États pour le modal d'impression
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printFilterType, setPrintFilterType] = useState('in_delivery');
  const [printDeliveryDate, setPrintDeliveryDate] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  
  // État pour le filtre de date de livraison (séparé de la date de création)
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Conserve la page distante affichée et indique si l'API possède une page suivante.
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, hasMore: false });

  // Fonction pour lire les filtres depuis l'URL
  const readFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);

    return {
      date: params.get('date') ? dayjs(params.get('date')) : null,
      deliveryDate: params.get('deliveryDate') ? dayjs(params.get('deliveryDate')) : null,
      deliveryPersonId: params.get('deliveryPersonId') || null,
      status: params.get('status') || null,
      customerId: params.get('customerId') || null,
      customerName: params.get('customerName') || '',
      tags: params.get('tags') ? params.get('tags').split(',') : [],
      searchText: params.get('searchText') || '',
      orderNumber: params.get('orderNumber') || '',
      deliveryNotesSearch: params.get('deliveryNotesSearch') || '',
      // Filtre par variante (depuis ProductList)
      variantId: params.get('variantId') || null,
      variantName: params.get('variantName') || '',
      productName: params.get('productName') || ''
    };
  };

  // Fonction pour mettre à jour l'URL avec les filtres actuels
  const updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams(location.search);
    
    // Mettre à jour les paramètres
    if (filters.date) {
      params.set('date', filters.date.format('YYYY-MM-DD'));
    } else {
      params.delete('date');
    }
    
    if (filters.deliveryDate) {
      params.set('deliveryDate', filters.deliveryDate.format('YYYY-MM-DD'));
    } else {
      params.delete('deliveryDate');
    }
    
    if (filters.deliveryPersonId) {
      params.set('deliveryPersonId', filters.deliveryPersonId);
    } else {
      params.delete('deliveryPersonId');
    }
    
    if (filters.status) {
      params.set('status', filters.status);
    } else {
      params.delete('status');
    }
    
    if (filters.customerId) {
      params.set('customerId', filters.customerId);
    } else {
      params.delete('customerId');
    }
    
    if (filters.customerName) {
      params.set('customerName', filters.customerName);
    } else {
      params.delete('customerName');
    }
    
    if (filters.tags && filters.tags.length > 0) {
      params.set('tags', filters.tags.join(','));
    } else {
      params.delete('tags');
    }
    
    if (filters.searchText) {
      params.set('searchText', filters.searchText);
    } else {
      params.delete('searchText');
    }

    if (filters.orderNumber) {
      params.set('orderNumber', filters.orderNumber);
    } else {
      params.delete('orderNumber');
    }

    if (filters.deliveryNotesSearch) {
      params.set('deliveryNotesSearch', filters.deliveryNotesSearch);
    } else {
      params.delete('deliveryNotesSearch');
    }

    // S'assurer que l'onglet est bien défini sur 'orders'
    params.set('tab', 'orders');
    
    // Mettre à jour l'URL sans recharger la page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  /**
   * Charge une page de commandes depuis l'API en demandant une commande
   * supplémentaire. Cette commande sert uniquement à savoir si une page suivante
   * existe, sans récupérer inutilement l'ensemble de l'historique.
   */
  const loadOrders = async (personId = selectedDeliveryPersonId, date = selectedDate, statusFilter = selectedStatus, customerId = selectedCustomerId, tags = selectedTags, deliveryDate = selectedDeliveryDate, variantId = selectedVariantId, page = 1, pageSize = pagination.pageSize, orderNumber = selectedOrderNumber, notesSearch = debouncedDeliveryNotesSearch) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pageSize;
      const limitWithProbe = pageSize + 1;

      /**
       * Met à jour la liste et les informations de pagination à partir des
       * commandes reçues. La dernière commande éventuelle est retirée de l'écran,
       * car elle ne sert qu'à confirmer l'existence de la page suivante. Les pages
       * précédentes sont conservées pour que le tableau Ant Design puisse naviguer
       * correctement entre les pages déjà consultées.
       */
      const updateOrdersPage = (loadedOrders) => {
        const hasMore = loadedOrders.length > pageSize;
        const visibleOrders = loadedOrders.slice(0, pageSize);

        setOrders((currentOrders) => (
          page === 1
            ? visibleOrders
            : [...currentOrders.slice(0, offset), ...visibleOrders]
        ));
        setPagination({ current: page, pageSize, hasMore });
      };

      // Si un filtre par variante est actif, utiliser l'API spécifique
      if (variantId) {
        console.log('Chargement des commandes pour la variante:', variantId);

        // Appeler l'API pour récupérer les commandes contenant cette variante
        const response = await ordersApi.getByVariant(variantId, {
          status: statusFilter,
          limit: limitWithProbe,
          offset,
          order_number: orderNumber || undefined,
          delivery_notes_search: notesSearch.trim() || undefined
        });

        console.log('response (by variant)', response);
        if (response && response.data && response.data.success) {
          updateOrdersPage(response.data.data || []);
        } else {
          throw new Error('Erreur lors du chargement des commandes par variante');
        }
      } else {
        // Comportement normal : récupérer toutes les commandes avec filtres
        const filters = {
          // Inclure les relations (customer et delivery_person) dans la réponse
          include_items: false, // On ne veut pas les items dans la liste
          order_by: 'created_at',
          ascending: false,
          limit: limitWithProbe,
          offset
        };

        // Transmettre le numéro tel quel pour préserver les grands identifiants BIGINT.
        if (orderNumber) {
          filters.order_number = orderNumber;
        }

        // La recherche est effectuée par l'API afin de couvrir toutes les pages de commandes.
        if (notesSearch.trim()) {
          filters.delivery_notes_search = notesSearch.trim();
        }

        // Filtre par livreur assigné
        if (personId) {
          filters.delivery_person_id = personId;
        }

        console.log('filters', filters);
        console.log('date', date);

        // Filtre par date de création
        if (date) {
          // Approche simple : utiliser directement le format de date
          const dateStr = date.format('YYYY-MM-DD');
          const startDate = `${dateStr} 00:00:00`;
          const endDate = `${dateStr} 23:59:59`;
          console.log('date original:', date.format('YYYY-MM-DD'));
          console.log('dateStr:', dateStr);
          console.log('startDate', startDate);
          console.log('endDate', endDate);
          filters.created_at_from = startDate;
          filters.created_at_to = endDate;
        }

        // Filtre par statut
        if (statusFilter) {
          filters.status = statusFilter;
        }

        // Filtre par client sélectionné
        if (customerId) {
          filters.customer_id = customerId;
        }

        // Filtre par tags
        if (tags && tags.length > 0) {
          filters.tags = tags.join(',');
        }

        // Filtre par date de livraison
        if (deliveryDate) {
          const dateStr = deliveryDate.format('YYYY-MM-DD');
          const startDate = `${dateStr} 00:00:00`;
          const endDate = `${dateStr} 23:59:59`;
          filters.delivery_date_from = startDate;
          filters.delivery_date_to = endDate;
        }

        // Appeler l'API pour récupérer les commandes avec filtres
        const response = await ordersApi.getAll(filters);
        console.log('response', response);
        if (response && response.data) {
          updateOrdersPage(response.data || []);
        } else {
          throw new Error('Erreur lors du chargement des commandes');
        }
      }

    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
      message.error("Erreur lors du chargement des commandes.");
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryPersons = async () => {
    try {
      setLoadingDeliveryPersons(true);
      
      // Utiliser l'API pour récupérer les livreurs actifs
      const response = await deliveryPersonsApi.getAll({ 
        is_active: true,
        order_by: 'name',
        ascending: true 
      });

      if (response && response.data) {
        setDeliveryPersons(response.data || []);
      } else {
        throw new Error('Erreur lors du chargement des livreurs');
      }
    } catch (error) {
      console.error('Error loading delivery persons:', error);
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoadingDeliveryPersons(false);
    }
  };

  /**
   * Restaure les filtres partagés dans l'URL au premier affichage, puis charge
   * les livreurs et la première page correspondante de commandes dans cet ordre.
   */
  useEffect(() => {
    const initializeFromUrl = async () => {
      const urlFilters = readFiltersFromUrl();

      // Définir tous les états des filtres
      setSelectedDate(urlFilters.date);
      setSelectedDeliveryDate(urlFilters.deliveryDate);
      setSelectedDeliveryPersonId(urlFilters.deliveryPersonId);
      setSelectedStatus(urlFilters.status);
      setSelectedCustomerId(urlFilters.customerId);
      setSelectedCustomerName(urlFilters.customerName);
      setSelectedTags(urlFilters.tags);
      setSearchText(urlFilters.searchText);
      setSelectedOrderNumber(urlFilters.orderNumber);
      setDeliveryNotesSearch(urlFilters.deliveryNotesSearch);
      setDebouncedDeliveryNotesSearch(urlFilters.deliveryNotesSearch);

      // Définir les états pour le filtre par variante
      setSelectedVariantId(urlFilters.variantId);
      setSelectedVariantName(urlFilters.variantName);
      setSelectedProductName(urlFilters.productName);

      // Charger les livreurs d'abord
      await loadDeliveryPersons();

      // Puis charger les commandes avec les filtres restaurés de l'URL
      await loadOrders(
        urlFilters.deliveryPersonId,
        urlFilters.date,
        urlFilters.status,
        urlFilters.customerId,
        urlFilters.tags,
        urlFilters.deliveryDate,
        urlFilters.variantId, // Ajouter le filtre par variante
        1,
        pagination.pageSize,
        urlFilters.orderNumber,
        urlFilters.deliveryNotesSearch
      );

      // Marquer comme initialisé pour éviter les doubles appels
      setIsInitialized(true);
    };

    initializeFromUrl();
  }, []);

  /**
   * Attend une courte pause après chaque saisie dans la recherche de notes avant
   * de lancer le filtrage distant. Cette temporisation réduit les appels réseau
   * tout en gardant une mise à jour rapide de la liste pour l'utilisateur.
   */
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedDeliveryNotesSearch(deliveryNotesSearch);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [deliveryNotesSearch]);

  /**
   * Repart de la première page dès qu'un filtre de liste change, afin que les
   * commandes affichées correspondent toujours au nouveau périmètre recherché.
   */
  useEffect(() => {
    // Ne pas déclencher pendant l'initialisation pour éviter les doubles appels
    if (!isInitialized) return;
    
    // Mettre à jour l'URL avec les filtres actuels
    updateUrlWithFilters({
      date: selectedDate,
      deliveryDate: selectedDeliveryDate,
      deliveryPersonId: selectedDeliveryPersonId,
      status: selectedStatus,
      customerId: selectedCustomerId,
      customerName: selectedCustomerName,
      tags: selectedTags,
      searchText: searchText,
      orderNumber: selectedOrderNumber,
      deliveryNotesSearch: debouncedDeliveryNotesSearch
    });
    
    loadOrders(selectedDeliveryPersonId, selectedDate, selectedStatus, selectedCustomerId, selectedTags, selectedDeliveryDate, selectedVariantId, 1, pagination.pageSize, selectedOrderNumber, debouncedDeliveryNotesSearch);
  }, [selectedDeliveryPersonId, selectedDate, selectedDeliveryDate, selectedStatus, selectedCustomerId, selectedTags, selectedVariantId, selectedOrderNumber, debouncedDeliveryNotesSearch, isInitialized]);

  /**
   * Synchronise dans l'URL le libellé du client et le texte de recherche, qui
   * n'impliquent pas à eux seuls un nouveau chargement de la liste des commandes.
   */
  useEffect(() => {
    // Ne pas déclencher pendant l'initialisation
    if (!isInitialized) return;
    
    updateUrlWithFilters({
      date: selectedDate,
      deliveryDate: selectedDeliveryDate,
      deliveryPersonId: selectedDeliveryPersonId,
      status: selectedStatus,
      customerId: selectedCustomerId,
      customerName: selectedCustomerName,
      tags: selectedTags,
      searchText: searchText,
      orderNumber: selectedOrderNumber,
      deliveryNotesSearch: debouncedDeliveryNotesSearch
    });
  }, [selectedCustomerName, searchText, isInitialized]);

  /**
   * Copie la page reçue dans la source du tableau ; le filtrage est déjà pris
   * en charge par l'API pour éviter de limiter la recherche aux données locales.
   */
  useEffect(() => {
    setFilteredOrders(orders);
  }, [orders]);

  /**
   * Charge la page demandée par le tableau tout en conservant les filtres actifs.
   * Si la taille de page change, le retour à la première page évite un décalage
   * vers une page qui n'existerait plus.
   */
  const handlePaginationChange = (page, pageSize) => {
    const targetPage = pageSize === pagination.pageSize ? page : 1;

    loadOrders(
      selectedDeliveryPersonId,
      selectedDate,
      selectedStatus,
      selectedCustomerId,
      selectedTags,
      selectedDeliveryDate,
      selectedVariantId,
      targetPage,
      pageSize,
      selectedOrderNumber,
      debouncedDeliveryNotesSearch
    );
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'pending': return 'warning';
      case 'confirmed': return 'processing';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      case 'completed': return 'success';
      case 'refunded': return 'volcano';
      default: return 'default';
    }
  };

  // Fonction pour traduire les statuts en français
  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouvelle';
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmée';
      case 'in_delivery': return 'En livraison';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      case 'completed': return 'Terminée';
      case 'refunded': return 'Remboursée';
      default: return status || 'N/A';
    }
  };

  const loadOrderItems = async (orderId, orderSource) => {
    if (!orderId || expandedDetails[orderId]?.items) return;

    setExpandedDetails(prev => ({ 
        ...prev, 
        [orderId]: { loading: true, items: [], error: null } 
    }));

    try {
        // Utiliser directement la source déjà connue
        const isShopifyOrder = orderSource === 'shopify';
        
        console.log(`Chargement des articles pour commande ${orderId}, source: ${orderSource}`);
        
        // Récupérer les articles de la commande avec leurs relations
        const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select(`
                id,
                product_name,
                product_price,
                quantity,
                notes,
                product_variant_id,
                external_product_id,
                product_variant: product_variants (
                    sku,
                    product: products (
                        name,
                        main_image
                    )
                )
            `)
            .eq('order_id', orderId);
            
        if (itemsError) throw itemsError;
        
        console.log("Articles bruts récupérés:", itemsData);
        
        // Pour les articles Shopify sans product_variant_id, chercher les produits par external_id
        let enrichedItems = [...itemsData];
        
        if (isShopifyOrder) {
            const itemsWithoutVariant = itemsData.filter(item => 
                !item.product_variant_id && item.external_product_id
            );
            
            console.log(`${itemsWithoutVariant.length} articles Shopify sans variant à enrichir`);
            
            // Traiter chaque article Shopify sans variant un par un
            for (const item of itemsWithoutVariant) {
                if (!item.external_product_id) continue;
                
                const shopifyProductId = `gid://shopify/Product/${item.external_product_id}`;
                console.log(`Recherche du produit avec external_id: ${shopifyProductId}`);
                
                const { data: productData, error: productError } = await supabase
                    .from('products')
                    .select('name, main_image')
                    .eq('external_id', shopifyProductId)
                    .maybeSingle();
                    
                if (!productError && productData) {
                    console.log(`Produit Shopify trouvé:`, productData);
                    
                    // Trouver l'index de l'article dans le tableau enrichi
                    const itemIndex = enrichedItems.findIndex(enrichedItem => enrichedItem.id === item.id);
                    
                    if (itemIndex !== -1) {
                        // Mettre à jour l'article avec les informations du produit
                        enrichedItems[itemIndex] = {
                            ...enrichedItems[itemIndex],
                            shopify_product: productData,
                            // Si product_name est vide, le remplacer par le nom du produit
                            product_name: enrichedItems[itemIndex].product_name || productData.name
                        };
                    }
                } else {
                    console.log(`Produit Shopify non trouvé pour external_product_id: ${item.external_product_id}`);
                }
            }
        }
        
        console.log("Articles enrichis:", enrichedItems);
        
        setExpandedDetails(prev => ({ 
            ...prev, 
            [orderId]: { loading: false, items: enrichedItems || [], error: null } 
        }));

    } catch (error) {
        console.error(`Error loading items for order ${orderId}:`, error);
        setExpandedDetails(prev => ({ 
            ...prev, 
            [orderId]: { loading: false, items: [], error: error } 
        }));
    }
  };

  const columns = [
    {
      title: 'N°',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 80,
      sorter: (a, b) => (a.order_number || 0) - (b.order_number || 0),
      render: (num) => num || 'N/A',
    },
    {
      title: 'Date Création',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      defaultSortOrder: 'ascend',
      // Masquer sur mobile très petit
      responsive: ['sm'],
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      width: 210,
      render: (customer) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{customer?.name || 'Client inconnu'}</div>
          <div className="text-xs text-gray-500 truncate">{customer?.phone || ''}</div>
        </div>
      ),
       sorter: (a, b) => (a.customer?.name || '').localeCompare(b.customer?.name || ''),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 90,
      align: 'right',
      render: (amount) => (
        <div className="text-right">
          <div className="font-medium">{Number(amount || 0).toFixed(0)}</div>
          <div className="text-xs text-gray-500">{getDeviseActive()}</div>
        </div>
      ),
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status) => (
            <Tag color={getOrderStatusColor(status)} className="text-xs">
              {getOrderStatusLabel(status)}
            </Tag>
        ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 90,
      // Masquer les tags sur mobile très petit
      responsive: ['md'],
      render: (tags) => (
        <div className="min-w-0">
          {tags && tags.length > 0 ? (
            tags.slice(0, 1).map((tag, index) => (
              <Tag key={index} color="blue" className="private-tag-accent text-xs mb-1">
                {tag}
              </Tag>
            ))
          ) : (
            <span className="text-gray-400 italic text-xs">Aucun</span>
          )}
          {tags && tags.length > 1 && (
            <Tag color="default" className="text-xs">+{tags.length - 1}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Livreur',
      dataIndex: 'delivery_person',
      key: 'delivery_person',
      width: 110,
      // Masquer sur tablette et plus petit
      responsive: ['lg'],
      render: (deliveryPerson) => (
          <Text type={deliveryPerson ? 'default' : 'secondary'} className="text-xs truncate block" style={{ fontStyle: deliveryPerson ? 'normal' : 'italic' }}>
              {deliveryPerson?.name || 'Non assigné'}
          </Text>
      ),
      sorter: (a, b) => (a.delivery_person?.name || '').localeCompare(b.delivery_person?.name || ''),
    },
    {
      title: 'Notes de livraison',
      dataIndex: 'delivery_notes',
      key: 'delivery_notes',
      width: 240,
      render: (deliveryNotes, record) => (
        <Input.TextArea
          key={`${record.id}-${deliveryNotes || ''}`}
          defaultValue={deliveryNotes || ''}
          autoSize={{ minRows: 1, maxRows: 2 }}
          placeholder="Ajouter une note…"
          disabled={savingDeliveryNotesOrderId === record.id}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onBlur={(event) => handleDeliveryNotesSave(record, event.target.value)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 70,
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            // Conserver les filtres actuels dans l'URL de destination
            const currentParams = new URLSearchParams(location.search);
            navigate(`/app/orders/${record.id}?returnFilters=${encodeURIComponent(currentParams.toString())}`);
          }}
          title="Voir les détails"
          className="flex items-center justify-center"
        >
          <span className="hidden sm:inline ml-1">Détails</span>
        </Button>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const { id: orderId, customer, delivery_address } = record;
    const details = expandedDetails[orderId];

    return (
      <div className="p-3 sm:p-4 bg-gray-50">
        <Title level={5} className="mb-3 text-sm sm:text-base">
          Détails Commande #{record.order_number || record.id}
        </Title>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informations client et livraison */}
            <div className="space-y-2">
                <div><strong>Client:</strong> {customer?.name || 'N/A'}</div>
                <div><strong>Téléphone:</strong> {customer?.phone || 'N/A'}</div>
                <div><strong>Adresse Livraison:</strong></div>
                <Text type="secondary" className="text-xs sm:text-sm block whitespace-pre-wrap">
                  {delivery_address || 'Non fournie'}
                </Text>
            </div>
            
            {/* Articles commandés */}
            <div>
                <div className="font-semibold mb-2">Articles commandés:</div>
                {details?.loading && (
                    <div className="text-center py-3">
                       <Spin indicator={<LoadingOutlined className="text-lg" spin />} /> Chargement...
                    </div>
                )}
                {details?.error && (
                    <Text type="danger">Erreur lors du chargement des articles.</Text>
                )}
                {!details?.loading && !details?.error && (
                    details?.items?.length > 0 ? (
                        <div className="space-y-2">
                            {details.items.map(item => (
                                <div key={item.id} className="pb-2 border-b border-gray-200 last:border-b-0">
                                    <div className="flex items-start gap-2">
                                        {/* Image du produit (si disponible) */}
                                        {(item.product_variant?.product?.main_image || item.shopify_product?.main_image) && (
                                            <img 
                                                src={item.product_variant?.product?.main_image || item.shopify_product?.main_image} 
                                                alt="Produit"
                                                className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded flex-shrink-0"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs sm:text-sm">
                                              <Text strong>{item.quantity} x</Text> 
                                              <span className="ml-1 truncate">{item.product_name || item.product_variant?.product?.name || 'Article inconnu'}</span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {Number(item.product_price || 0).toFixed(0)} {getDeviseActive()}
                                              {item.product_variant?.sku && (
                                                <Text code className="ml-2 text-xs">{item.product_variant.sku}</Text>
                                              )}
                                            </div>
                                            {item.notes && (
                                              <Text italic className="text-xs text-gray-600 block mt-1">
                                                Note: {item.notes}
                                              </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Text type="secondary" className="text-xs sm:text-sm">
                         {details && details.items ? 
                           'Aucun article trouvé pour cette commande.' 
                           : 'Cliquez pour charger les articles.'} 
                        </Text>
                    )
                )}
            </div>
        </div>
      </div>
    );
  };

  const handleExpand = (expanded, record) => {
      if (expanded) {
          if (!expandedDetails[record.id]) {
              loadOrderItems(record.id, record.source);
          }
      }
  };

  // Fonction pour rechercher les clients
  const searchClients = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setClientSearchOptions([]);
      return;
    }

    try {
      setClientSearchLoading(true);
      
      // Rechercher les clients via l'API
      const response = await clientsApi.getAll({
        name: searchValue,
        phone: searchValue,
        limit: 10
      });
      console.log('response', response);
      if (response && response.data) {
        const options = response.data.customers.map(client => ({
          value: client.id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{client.name}</div>
                <div style={{ fontSize: '0.8em', color: '#666' }}>{client.phone}</div>
              </div>
              <UserOutlined style={{ color: PRIMARY_COLOR }} />
            </div>
          ),
          client: client
        }));
        setClientSearchOptions(options);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error);
      message.error('Erreur lors de la recherche de clients');
    } finally {
      setClientSearchLoading(false);
    }
  };

  // Fonction pour gérer la sélection d'un client
  const handleClientSelect = (value, option) => {
    if (option && option.client) {
      setSelectedCustomerId(value);
      setSelectedCustomerName(option.client.name);
      setSearchText(`${option.client.name} (${option.client.phone})`);
    }
  };

  // Fonction pour gérer le changement de texte dans l'AutoComplete
  const handleClientSearch = (value) => {
    setSearchText(value);
    
    // Si on efface le texte, réinitialiser la sélection
    if (!value) {
      setSelectedCustomerId(null);
      setSelectedCustomerName('');
      setClientSearchOptions([]);
      return;
    }

    // Débounce la recherche
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchClients(value);
    }, 300);
    
    setSearchTimeout(timeout);
  };

  /**
   * Mémorise le texte tapé pour la recherche dans les notes de livraison.
   * La temporisation associée à cet état déclenche ensuite la requête serveur
   * seulement après une pause de saisie, afin de préserver la fluidité de la page.
   */
  const handleDeliveryNotesSearchChange = (event) => {
    setDeliveryNotesSearch(event.target.value);
  };

  /**
   * Met à jour le filtre de numéro de commande sans accepter de caractère non
   * numérique. Une valeur vide retire le filtre ; chaque valeur valide déclenche
   * ensuite le rechargement centralisé de la liste via le useEffect des filtres.
   */
  const handleOrderNumberChange = (event) => {
    const orderNumber = event.target.value;

    if (/^\d*$/.test(orderNumber)) {
      setSelectedOrderNumber(orderNumber);
    }
  };

  /**
   * Indique si une commande peut être incluse dans une annulation groupée. Les
   * statuts exclus sont identiques à ceux du modal de détail afin de ne pas
   * proposer une action que le service d'annulation refuserait ensuite.
   */
  const canBulkCancelOrder = (order) => {
    return order && !['cancelled', 'completed', 'delivered', 'refunded'].includes(order.status);
  };

  /**
   * Détermine si la commande peut être encaissée avec le même traitement que
   * l’action unitaire. Les commandes annulées, déjà terminées ou sans montant
   * ne sont pas proposées afin d’éviter un appel API qui serait refusé.
   */
  const canBulkMarkAsPaidOrder = (order) => {
    return order
      && !['cancelled', 'completed', 'refunded'].includes(order.status)
      && Number(order.total_amount) > 0;
  };

  /**
   * Autorise la sélection des commandes terminées afin de calculer le montant
   * attendu. Les commandes annulées ou remboursées restent exclues, car elles
   * ne doivent jamais être incluses dans une action d'attribution ou de paiement.
   */
  const canSelectOrderForBulkAction = (order) => {
    return order && !['cancelled', 'refunded'].includes(order.status);
  };

  /**
   * Ouvre l'attribution groupée avec les mêmes valeurs initiales que la fiche
   * de commande. Une valeur commune est demandée car le livreur, la date, les
   * frais et le mode de message seront appliqués à chaque commande sélectionnée.
   */
  const showBulkAssignmentModal = () => {
    if (selectedOrderIds.length === 0) {
      message.warning('Sélectionnez au moins une commande à attribuer.');
      return;
    }

    bulkAssignmentForm.setFieldsValue({
      delivery_person_id: null,
      delivery_date: dayjs(),
      shipping_fee: 0,
      delivery_message_mode: 'scheduled'
    });
    setIsBulkAssignmentModalVisible(true);
  };

  /**
   * Attribue le même livreur aux commandes sélectionnées en utilisant
   * l'endpoint groupé déjà employé par la page détail. Après succès, la liste
   * est rechargée afin d'afficher le nouveau livreur, la date et les frais.
   * Les échecs de programmation de message sont signalés séparément :
   * l'attribution elle-même reste effective même si une notification échoue.
   */
  const handleBulkAssignOrders = async () => {
    try {
      const values = await bulkAssignmentForm.validateFields();
      const deliveryDate = values.delivery_date
        ? values.delivery_date.format('YYYY-MM-DD')
        : null;

      setBulkAssignmentLoading(true);
      const response = await ordersApi.assignOrders(
        selectedOrderIds,
        values.delivery_person_id,
        {
          delivery_date: deliveryDate,
          shipping_fee: values.shipping_fee || 0,
          delivery_message_mode: values.delivery_message_mode
        }
      );

      if (!response) {
        throw new Error('L’attribution des commandes a échoué.');
      }

      // Lit le résultat détaillé renvoyé par le serveur afin de ne pas masquer
      // une programmation MSGLIVREUR échouée derrière le succès de l'attribution.
      const failedMessages = response.data?.messages_scheduled?.filter(
        (scheduledMessage) => !scheduledMessage.success
      ) || [];

      if (failedMessages.length > 0) {
        message.warning(
          `${selectedOrderIds.length} commande(s) attribuée(s), mais ${failedMessages.length} message(s) automatique(s) n’ont pas été programmé(s).`
        );
      } else {
        message.success(`${selectedOrderIds.length} commande(s) attribuée(s) avec succès.`);
      }
      setIsBulkAssignmentModalVisible(false);
      bulkAssignmentForm.resetFields();
      setSelectedOrderIds([]);
      await loadOrders(
        selectedDeliveryPersonId,
        selectedDate,
        selectedStatus,
        selectedCustomerId,
        selectedTags,
        selectedDeliveryDate,
        selectedVariantId,
        1,
        pagination.pageSize,
        selectedOrderNumber
      );
    } catch (error) {
      console.error('Erreur lors de l’attribution groupée des commandes:', error);
      if (!error.errorFields) {
        message.error('Erreur lors de l’attribution du livreur aux commandes sélectionnées.');
      }
    } finally {
      setBulkAssignmentLoading(false);
    }
  };

  /**
   * Ouvre une calculatrice locale dont l'expression démarre avec le montant net
   * des commandes sélectionnées. Ce montant peut ensuite être complété sans
   * déclencher d'appel API ni modifier une commande.
   */
  const showCalculator = () => {
    const initialAmount = String(Math.round(selectedAmountToReceive));
    setCalculatorExpression(initialAmount);
    setCalculatorResult(initialAmount);
    setIsCalculatorVisible(true);
  };

  /**
   * Évalue une expression limitée aux chiffres et aux opérateurs arithmétiques.
   * La validation interdit toute autre syntaxe avant le calcul, puis le résultat
   * est refusé s'il n'est pas un nombre fini utilisable dans la calculatrice.
   */
  const evaluateCalculatorExpression = (expression) => {
    if (!expression || !/^[0-9+\-*/().\s]+$/.test(expression)) {
      throw new Error('Expression invalide');
    }

    const result = Function(`"use strict"; return (${expression})`)();
    if (!Number.isFinite(result)) {
      throw new Error('Résultat invalide');
    }

    return result;
  };

  /**
   * Traite les touches de la calculatrice : ajout d'un symbole, effacement ou
   * calcul du résultat. Les erreurs restent dans le modal pour ne pas perturber
   * la sélection de commandes actuellement affichée.
   */
  const handleCalculatorKey = (key) => {
    if (key === 'clear') {
      setCalculatorExpression('');
      setCalculatorResult(null);
      return;
    }

    if (key === 'backspace') {
      setCalculatorExpression((expression) => expression.slice(0, -1));
      return;
    }

    if (key === '=') {
      try {
        const result = evaluateCalculatorExpression(calculatorExpression);
        setCalculatorResult(String(result));
      } catch (error) {
        setCalculatorResult('Erreur');
      }
      return;
    }

    setCalculatorExpression((expression) => `${expression}${key}`);
    setCalculatorResult(null);
  };

  /**
   * Enregistre une note de livraison directement depuis le tableau. La requête
   * ne part que si le texte a changé, puis l’état local est mis à jour afin que
   * la valeur affichée reste synchronisée sans recharger la liste entière.
   */
  const handleDeliveryNotesSave = async (order, note) => {
    const deliveryNotes = note.trim();

    if (deliveryNotes === (order.delivery_notes || '')) {
      return;
    }

    try {
      setSavingDeliveryNotesOrderId(order.id);
      const response = await ordersApi.update(order.id, { delivery_notes: deliveryNotes });

      if (!response) {
        throw new Error('La note de livraison n’a pas pu être enregistrée.');
      }

      setOrders((currentOrders) => currentOrders.map((currentOrder) => (
        currentOrder.id === order.id
          ? { ...currentOrder, delivery_notes: deliveryNotes }
          : currentOrder
      )));
      message.success(`Note de livraison enregistrée pour la commande ${order.order_number}.`);
    } catch (error) {
      console.error(`Erreur lors de l’enregistrement de la note de livraison ${order.id}:`, error);
      message.error(`Impossible d’enregistrer la note de la commande ${order.order_number}.`);
    } finally {
      setSavingDeliveryNotesOrderId(null);
    }
  };

  /**
   * Ouvre le modal d'annulation après avoir vérifié qu'au moins une commande
   * sélectionnée est encore annulable. Le formulaire est réinitialisé pour ne
   * jamais réutiliser un motif ou une option de blocage d'une action précédente.
   */
  const showBulkCancelModal = () => {
    const cancellableOrders = orders.filter((order) => (
      selectedOrderIds.includes(order.id) && canBulkCancelOrder(order)
    ));

    if (cancellableOrders.length === 0) {
      message.warning('Sélectionnez au moins une commande pouvant être annulée.');
      return;
    }

    bulkCancelForm.resetFields();
    setIsBulkCancelModalVisible(true);
  };

  /**
   * Annule les commandes sélectionnées une par une via l'API existante. Chaque
   * commande conserve ses propres notes de livraison, auxquelles le motif commun
   * est ajouté, afin d'appliquer exactement les règles métier de la page détail.
   */
  const handleBulkCancelOrders = async () => {
    try {
      const values = await bulkCancelForm.validateFields();
      const ordersToCancel = orders.filter((order) => (
        selectedOrderIds.includes(order.id) && canBulkCancelOrder(order)
      ));
      const cancelReason = values.reason?.trim() || 'Annulation sans motif spécifié';
      const timestamp = dayjs().format('DD/MM/YYYY HH:mm:ss');
      const failedOrderNumbers = [];

      setBulkCancellationLoading(true);

      for (const order of ordersToCancel) {
        const cancellationNote = `[${timestamp}] Annulation: ${cancelReason}`;
        const deliveryNotes = order.delivery_notes
          ? `${order.delivery_notes}\n${cancellationNote}`
          : cancellationNote;
        const response = await ordersApi.cancelOrder(order.id, {
          reason: cancelReason,
          block_customer: values.block_customer || false,
          delivery_notes: deliveryNotes
        });

        if (!response) {
          failedOrderNumbers.push(order.order_number || order.id);
        }
      }

      const successfulCount = ordersToCancel.length - failedOrderNumbers.length;
      if (successfulCount > 0) {
        message.success(`${successfulCount} commande(s) annulée(s) avec succès.`);
      }
      if (failedOrderNumbers.length > 0) {
        message.error(`Impossible d'annuler : ${failedOrderNumbers.join(', ')}.`);
      }

      setIsBulkCancelModalVisible(false);
      bulkCancelForm.resetFields();
      setSelectedOrderIds([]);
      await loadOrders(
        selectedDeliveryPersonId,
        selectedDate,
        selectedStatus,
        selectedCustomerId,
        selectedTags,
        selectedDeliveryDate,
        selectedVariantId,
        1,
        pagination.pageSize,
        selectedOrderNumber
      );
    } catch (error) {
      console.error('Erreur lors de l’annulation groupée des commandes:', error);
      if (!error.errorFields) {
        message.error('Erreur lors de l’annulation groupée des commandes.');
      }
    } finally {
      setBulkCancellationLoading(false);
    }
  };

  /**
   * Ouvre le formulaire de paiement après avoir conservé uniquement les
   * commandes réellement encaissables. Le montant de chaque commande reste
   * son montant total : seule la méthode de paiement est commune au lot.
   */
  const showBulkPaymentModal = () => {
    const payableOrders = orders.filter((order) => (
      selectedOrderIds.includes(order.id) && canBulkMarkAsPaidOrder(order)
    ));

    if (payableOrders.length === 0) {
      message.warning('Sélectionnez au moins une commande pouvant être marquée comme payée.');
      return;
    }

    bulkPaymentForm.resetFields();
    setIsBulkPaymentModalVisible(true);
  };

  /**
   * Encaisse chaque commande sélectionnée via l’endpoint unitaire existant.
   * Cela préserve la totalité des effets métier déjà testés : statut terminé,
   * points client, stock, messages automatiques et événement Meta Purchase.
   */
  const handleBulkMarkAsPaid = async () => {
    try {
      const values = await bulkPaymentForm.validateFields();
      const ordersToPay = orders.filter((order) => (
        selectedOrderIds.includes(order.id) && canBulkMarkAsPaidOrder(order)
      ));
      const failedOrderNumbers = [];

      setBulkPaymentLoading(true);

      for (const order of ordersToPay) {
        const response = await ordersApi.markOrderAsPaid(order.id, {
          amount: Number(order.total_amount),
          method: values.method,
          notes: values.notes?.trim() || 'Paiement groupé depuis le backoffice'
        });

        if (!response) {
          failedOrderNumbers.push(order.order_number || order.id);
        }
      }

      const successfulCount = ordersToPay.length - failedOrderNumbers.length;
      if (successfulCount > 0) {
        message.success(`${successfulCount} commande(s) marquée(s) comme payée(s).`);
      }
      if (failedOrderNumbers.length > 0) {
        message.error(`Impossible de marquer comme payées : ${failedOrderNumbers.join(', ')}.`);
      }

      setIsBulkPaymentModalVisible(false);
      bulkPaymentForm.resetFields();
      setSelectedOrderIds([]);
      await loadOrders(
        selectedDeliveryPersonId,
        selectedDate,
        selectedStatus,
        selectedCustomerId,
        selectedTags,
        selectedDeliveryDate,
        selectedVariantId,
        1,
        pagination.pageSize,
        selectedOrderNumber
      );
    } catch (error) {
      console.error('Erreur lors du paiement groupé des commandes:', error);
      if (!error.errorFields) {
        message.error('Erreur lors du paiement groupé des commandes.');
      }
    } finally {
      setBulkPaymentLoading(false);
    }
  };

  /**
   * Ouvre le modal de report uniquement lorsqu’au moins une commande
   * sélectionnée est encore active. Les mêmes statuts exclus que pour
   * l’annulation empêchent de reprogrammer une commande déjà finalisée.
   */
  const showBulkRescheduleModal = () => {
    const reschedulableOrders = orders.filter((order) => (
      selectedOrderIds.includes(order.id) && canBulkCancelOrder(order)
    ));

    if (reschedulableOrders.length === 0) {
      message.warning('Sélectionnez au moins une commande pouvant être reportée.');
      return;
    }

    bulkRescheduleForm.resetFields();
    setIsBulkRescheduleModalVisible(true);
  };

  /**
   * Applique la nouvelle date de livraison à chaque commande sélectionnée via
   * l’API existante. Aucun statut, paiement, stock ou autre champ métier n’est
   * modifié : le report se limite strictement à `delivery_date`.
   */
  const handleBulkRescheduleOrders = async () => {
    try {
      const values = await bulkRescheduleForm.validateFields();
      const reschedulableOrders = orders.filter((order) => (
        selectedOrderIds.includes(order.id) && canBulkCancelOrder(order)
      ));
      const deliveryDate = values.delivery_date.format('YYYY-MM-DD');
      const failedOrderNumbers = [];

      setBulkRescheduleLoading(true);

      for (const order of reschedulableOrders) {
        const response = await ordersApi.update(order.id, { delivery_date: deliveryDate });

        if (!response) {
          failedOrderNumbers.push(order.order_number || order.id);
        }
      }

      const successfulCount = reschedulableOrders.length - failedOrderNumbers.length;
      if (successfulCount > 0) {
        message.success(`${successfulCount} livraison(s) reportée(s) au ${values.delivery_date.format('DD/MM/YYYY')}.`);
      }
      if (failedOrderNumbers.length > 0) {
        message.error(`Impossible de reporter : ${failedOrderNumbers.join(', ')}.`);
      }

      setIsBulkRescheduleModalVisible(false);
      bulkRescheduleForm.resetFields();
      setSelectedOrderIds([]);
      await loadOrders(
        selectedDeliveryPersonId,
        selectedDate,
        selectedStatus,
        selectedCustomerId,
        selectedTags,
        selectedDeliveryDate,
        selectedVariantId,
        1,
        pagination.pageSize,
        selectedOrderNumber
      );
    } catch (error) {
      console.error('Erreur lors du report groupé des livraisons:', error);
      if (!error.errorFields) {
        message.error('Erreur lors du report groupé des livraisons.');
      }
    } finally {
      setBulkRescheduleLoading(false);
    }
  };

  // Fonction pour ouvrir le modal d'impression
  const openPrintModal = () => {
    setPrintModalVisible(true);
  };

  // Fonction pour fermer le modal d'impression
  const closePrintModal = () => {
    setPrintModalVisible(false);
    setPrintFilterType('in_delivery');
    setPrintDeliveryDate(null);
  };

  // Fonction pour obtenir une description des filtres actuels
  const getCurrentFiltersDescription = () => {
    const filters = [];
    
    if (selectedDate) {
      filters.push(`Date création: ${selectedDate.format('DD/MM/YYYY')}`);
    }
    if (selectedDeliveryDate) {
      filters.push(`Date livraison: ${selectedDeliveryDate.format('DD/MM/YYYY')}`);
    }
    if (selectedStatus) {
      const statusLabels = {
        'new': 'Nouvelle',
        'pending': 'En attente', 
        'confirmed': 'Confirmée',
        'in_delivery': 'En livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée',
        'completed': 'Terminée',
        'refunded': 'Remboursée'
      };
      filters.push(`Statut: ${statusLabels[selectedStatus] || selectedStatus}`);
    }
    if (selectedDeliveryPersonId && deliveryPersons.length > 0) {
      const deliveryPerson = deliveryPersons.find(p => p.id === selectedDeliveryPersonId);
      filters.push(`Livreur: ${deliveryPerson?.name || 'Sélectionné'}`);
    }
    if (selectedCustomerName) {
      filters.push(`Client: ${selectedCustomerName}`);
    }
    if (selectedTags && selectedTags.length > 0) {
      filters.push(`Tags: ${selectedTags.join(', ')}`);
    }
    
    return filters.length > 0 
      ? filters.join(' • ')
      : 'Aucun filtre appliqué (toutes les commandes)';
  };

  // Fonction pour construire l'objet des filtres actuels
  const getCurrentFiltersObject = () => {
    const filters = {};
    
    if (selectedStatus) {
      filters.status = selectedStatus;
    }
    if (selectedCustomerId) {
      filters.customer_id = selectedCustomerId;
    }
    if (selectedDeliveryPersonId) {
      filters.delivery_person_id = selectedDeliveryPersonId;
    }
    if (selectedDate) {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      filters.created_at_from = `${dateStr} 00:00:00`;
      filters.created_at_to = `${dateStr} 23:59:59`;
    }
    if (selectedDeliveryDate) {
      const dateStr = selectedDeliveryDate.format('YYYY-MM-DD');
      filters.delivery_date_from = `${dateStr} 00:00:00`;
      filters.delivery_date_to = `${dateStr} 23:59:59`;
    }
    if (selectedTags && selectedTags.length > 0) {
      // Filtrer et nettoyer les tags avant envoi (supprimer les valeurs null/undefined/vides)
      const cleanedTags = selectedTags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '');
      if (cleanedTags.length > 0) {
        filters.tags = cleanedTags;
      }
    }
    
    return filters;
  };

  // Fonction pour lancer l'impression
  const handlePrint = async () => {
    try {
      setPrintLoading(true);

      // Validation des données
      if (printFilterType === 'by_date' && !printDeliveryDate) {
        message.error('Veuillez sélectionner une date de livraison.');
        return;
      }

      // Préparer les paramètres selon le type de filtre
      let deliveryDateStr = null;
      let currentFiltersObj = null;
      
      if (printFilterType === 'by_date') {
        deliveryDateStr = printDeliveryDate.format('YYYY-MM-DD');
      } else if (printFilterType === 'current_filters') {
        currentFiltersObj = getCurrentFiltersObject();
      }

      // Appel API pour récupérer les données d'impression
      const response = await ordersApi.getPrintOrdersList(printFilterType, deliveryDateStr, currentFiltersObj);

      if (!response || !response.data) {
        message.error('Erreur lors de la récupération des données d\'impression.');
        return;
      }

      const { orders: printOrders, filter_type, filter_description, delivery_date, total_count } = response.data;

      if (printOrders.length === 0) {
        message.warning('Aucune commande trouvée pour les critères sélectionnés.');
        return;
      }

      // Générer le contenu HTML pour l'impression
      const printContent = generatePrintContent(printOrders, filter_type, filter_description, delivery_date, total_count);

      // Ouvrir une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Attendre que le contenu soit chargé puis lancer l'impression
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };

      message.success(`Liste de ${total_count} commande(s) générée pour impression.`);
      closePrintModal();

    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      message.error('Erreur lors de la génération de la liste d\'impression.');
    } finally {
      setPrintLoading(false);
    }
  };

  // Fonction pour générer le contenu HTML d'impression
  const generatePrintContent = (orders, filterType, filterDescription, deliveryDate, totalCount) => {
    let titleText = '';
    
    if (filterType === 'in_delivery') {
      titleText = 'Liste des commandes en livraison';
    } else if (filterType === 'by_date') {
      titleText = `Liste des commandes prévues pour le ${dayjs(deliveryDate).format('DD/MM/YYYY')}`;
    } else if (filterType === 'current_filters') {
      titleText = 'Liste des commandes filtrées';
    }

    const currentDate = dayjs().format('DD/MM/YYYY HH:mm');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${titleText}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12px;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            font-size: 18px;
            color: #333;
          }
          .header p {
            margin: 5px 0;
            color: #666;
          }
          .summary {
            margin-bottom: 20px;
            text-align: center;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            border: 1px solid #333;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 10px;
          }
          @media print {
            body {
              margin: 0;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${titleText}</h1>
          <p>Généré le ${currentDate}</p>
          ${filterDescription ? `<p style="margin-top: 10px; font-style: italic; color: #666;">${filterDescription}</p>` : ''}
        </div>
        
        <div class="summary">
          <p><strong>Nombre total de commandes: ${totalCount}</strong></p>
        </div>

        <table>
          <thead>
            <tr>
              <th>Info Commande</th>
              <th>Info commande</th>
              <th>Téléphone</th>
              <th>Lieu de Livraison</th>
              <th>Prix ({getDeviseActive()})</th>
            </tr>
          </thead>
          <tbody>
            ${orders.map(order => `
              <tr>
                <td></td>
                <td></td>
                <td>${order.customer_phone}</td>
                <td>${order.delivery_address}</td>
                <td style="text-align: right;">${order.total_amount}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Document généré automatiquement par le système de gestion des commandes</p>
        </div>
      </body>
      </html>
    `;
  };

  // Calcule le montant net attendu sans modifier les montants enregistrés dans les commandes.
  const selectedOrders = orders.filter((order) => selectedOrderIds.includes(order.id));
  const selectedAmountToReceive = selectedOrders.reduce((total, order) => (
    total + (Number(order.total_amount) || 0) - (Number(order.shipping_fee) || 0)
  ), 0);
  const hasSelectedOrdersPayable = selectedOrders.every(canBulkMarkAsPaidOrder);
  const hasSelectedOrdersCancellable = selectedOrders.every(canBulkCancelOrder);
  const hasSelectedOrdersAssignable = selectedOrders.every((order) => order.status !== 'completed');

  return (
    <div className={`${window.innerWidth < 768 ? '' : 'p-6'}`}>
      <Card>
        <Title level={4} style={{ marginBottom: '10px' }}>Liste des Commandes</Title>

        {/* Filtres responsive */}
        <div className="mb-5">
          {/* Version desktop : une seule ligne */}
          <div className="hidden lg:flex flex-wrap gap-3 mb-3">
            <DatePicker 
              placeholder="Date de création"
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY" 
              allowClear
              className="w-auto min-w-[150px]"
            />
            <DatePicker 
              placeholder="Date de livraison"
              value={selectedDeliveryDate}
              onChange={setSelectedDeliveryDate}
              format="DD/MM/YYYY" 
              allowClear
              className="w-auto min-w-[150px]"
            />
            <Select
              className="w-auto min-w-[160px]"
              placeholder="Livreur"
              loading={loadingDeliveryPersons}
              value={selectedDeliveryPersonId}
              onChange={setSelectedDeliveryPersonId}
              allowClear
            >
              {deliveryPersons.map(person => (
                  <Option key={person.id} value={person.id}>
                      {person.name}
                  </Option>
              ))}
            </Select>
            <Select
              className="w-auto min-w-[130px]"
              placeholder="Statut"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
            >
              <Option value="new">Nouvelle</Option>
              <Option value="confirmed">Confirmée</Option>
              <Option value="pending">En attente</Option>
              <Option value="in_delivery">En livraison</Option>
              <Option value="delivered">Livrée</Option>
              <Option value="completed">Terminée</Option>
              <Option value="cancelled">Annulée</Option>
              <Option value="refunded">Remboursée</Option>
            </Select>
            <Select
              mode="tags"
              className="w-auto min-w-[180px] flex-1"
              placeholder="Tags"
              value={selectedTags}
              onChange={(tags) => {
                // Normaliser les tags en minuscules et supprimer les doublons
                const normalizedTags = [...new Set(tags.map(tag => tag.toLowerCase().trim()))].filter(tag => tag);
                setSelectedTags(normalizedTags);
              }}
              allowClear
              tokenSeparators={[',']}
              filterOption={(input, option) => {
                // Recherche insensible à la casse dans les options
                return option.value.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {/* Options prédéfinies communes */}
              <Option value="urgent">Urgent</Option>
              <Option value="fragile">Fragile</Option>
              <Option value="gros-volume">Gros volume</Option>
              <Option value="livraison-express">Livraison express</Option>
              <Option value="paiement-cash">Paiement cash</Option>
              <Option value="client-vip">Client VIP</Option>
            </Select>
            {/* La recherche est volontairement séparée du client pour cibler le numéro unique de commande. */}
            <Input
              className="w-auto min-w-[170px]"
              placeholder="N° de commande"
              prefix={<SearchOutlined />}
              value={selectedOrderNumber}
              inputMode="numeric"
              onChange={handleOrderNumberChange}
              allowClear
            />
            {/* Recherche textuelle côté serveur pour retrouver une note, même hors de la page courante. */}
            <Input
              className="w-auto min-w-[220px] flex-1"
              placeholder="Rechercher dans les notes de livraison..."
              prefix={<SearchOutlined />}
              value={deliveryNotesSearch}
              onChange={handleDeliveryNotesSearchChange}
              aria-label="Rechercher dans les notes de livraison"
              allowClear
            />
            <AutoComplete
              className="w-auto min-w-[200px] flex-1"
              placeholder="Rechercher un client..."
              value={searchText}
              options={clientSearchOptions}
              onSearch={handleClientSearch}
              onSelect={handleClientSelect}
              notFoundContent={clientSearchLoading ? <Spin size="small" /> : 'Aucun client trouvé'}
              allowClear
              onClear={() => {
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSearchText('');
                setClientSearchOptions([]);
              }}
            />
          </div>

          {/* Version mobile/tablette : bloc repliable (fermé par défaut) */}
          <div className="lg:hidden">
            <Button
              block
              className="mb-3"
              icon={<FilterOutlined />}
              onClick={() => setShowMobileFilters((prev) => !prev)}
              aria-expanded={showMobileFilters}
            >
              Filtres
              {/* Nombre de filtres actifs, visible même quand le bloc est replié */}
              <Badge
                count={[
                  selectedDate, selectedDeliveryDate, selectedDeliveryPersonId, selectedStatus,
                  selectedTags?.length > 0, selectedOrderNumber, deliveryNotesSearch, selectedCustomerId
                ].filter(Boolean).length}
                className="ml-2"
              />
              {showMobileFilters ? <UpOutlined className="ml-2" /> : <DownOutlined className="ml-2" />}
            </Button>
            <div className={showMobileFilters ? 'block' : 'hidden'}>
            {/* Première ligne - Filtres principaux */}
            <div className="flex flex-wrap gap-3 mb-3">
              <DatePicker 
                placeholder="Date de création"
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY" 
                allowClear
                className="w-full sm:w-auto min-w-[150px]"
              />
              <DatePicker 
                placeholder="Date de livraison"
                value={selectedDeliveryDate}
                onChange={setSelectedDeliveryDate}
                format="DD/MM/YYYY" 
                allowClear
                className="w-full sm:w-auto min-w-[150px]"
              />
              <Select
                className="w-full sm:w-auto min-w-[160px]"
                placeholder="Livreur"
                loading={loadingDeliveryPersons}
                value={selectedDeliveryPersonId}
                onChange={setSelectedDeliveryPersonId}
                allowClear
              >
                {deliveryPersons.map(person => (
                    <Option key={person.id} value={person.id}>
                        {person.name}
                    </Option>
                ))}
              </Select>
              <Select
                className="w-full sm:w-auto min-w-[130px]"
                placeholder="Statut"
                value={selectedStatus}
                onChange={setSelectedStatus}
                allowClear
              >
                <Option value="new">Nouvelle</Option>
                <Option value="confirmed">Confirmée</Option>
                <Option value="pending">En attente</Option>
                <Option value="in_delivery">En livraison</Option>
                <Option value="delivered">Livrée</Option>
                <Option value="completed">Terminée</Option>
                <Option value="cancelled">Annulée</Option>
                <Option value="refunded">Remboursée</Option>
              </Select>
            </div>
            
            {/* Deuxième ligne - Filtres avancés */}
            <div className="flex flex-wrap gap-3 mb-3">
              <Select
                mode="tags"
                className="w-full sm:w-auto min-w-[180px] flex-1 sm:flex-none"
                placeholder="Tags"
                value={selectedTags}
                onChange={(tags) => {
                  // Normaliser les tags en minuscules et supprimer les doublons
                  const normalizedTags = [...new Set(tags.map(tag => tag.toLowerCase().trim()))].filter(tag => tag);
                  setSelectedTags(normalizedTags);
                }}
                allowClear
                tokenSeparators={[',']}
                filterOption={(input, option) => {
                  // Recherche insensible à la casse dans les options
                  return option.value.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {/* Options prédéfinies communes */}
                <Option value="urgent">Urgent</Option>
                <Option value="fragile">Fragile</Option>
                <Option value="gros-volume">Gros volume</Option>
                <Option value="livraison-express">Livraison express</Option>
                <Option value="paiement-cash">Paiement cash</Option>
                <Option value="client-vip">Client VIP</Option>
              </Select>
              {/* La recherche par numéro utilise uniquement des chiffres, comme la colonne BIGINT de la base. */}
              <Input
                className="w-full sm:w-auto min-w-[170px] flex-1 sm:flex-none"
                placeholder="N° de commande"
                prefix={<SearchOutlined />}
                value={selectedOrderNumber}
                inputMode="numeric"
                onChange={handleOrderNumberChange}
                allowClear
              />
              {/* Cette saisie recherche un mot ou une phrase dans les notes de livraison. */}
              <Input
                className="w-full sm:w-auto min-w-[220px] flex-1 sm:flex-none"
                placeholder="Rechercher dans les notes de livraison..."
                prefix={<SearchOutlined />}
                value={deliveryNotesSearch}
                onChange={handleDeliveryNotesSearchChange}
                aria-label="Rechercher dans les notes de livraison"
                allowClear
              />
              <AutoComplete
                className="w-full sm:w-auto min-w-[200px] flex-1 sm:flex-none"
                placeholder="Rechercher un client..."
                value={searchText}
                options={clientSearchOptions}
                onSearch={handleClientSearch}
                onSelect={handleClientSelect}
                notFoundContent={clientSearchLoading ? <Spin size="small" /> : 'Aucun client trouvé'}
                allowClear
                onClear={() => {
                  setSelectedCustomerId(null);
                  setSelectedCustomerName('');
                  setSearchText('');
                  setClientSearchOptions([]);
                }}
              />
            </div>
            </div>
          </div>
          
          {/* Boutons de réinitialisation et d'impression */}
          <div className="flex flex-wrap gap-3 justify-end">
            <Button
              onClick={() => {
                setSelectedDate(null);
                setSelectedDeliveryDate(null);
                setSelectedDeliveryPersonId(null);
                setSelectedStatus(null);
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSelectedTags([]);
                setSearchText('');
                setSelectedOrderNumber('');
                setDeliveryNotesSearch('');
                setClientSearchOptions([]);
                // Réinitialiser également le filtre de variante
                setSelectedVariantId(null);
                setSelectedVariantName('');
                setSelectedProductName('');
                // Supprimer les query params de variante de l'URL
                const params = new URLSearchParams(location.search);
                params.delete('variantId');
                params.delete('variantName');
                params.delete('productName');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
              }}
              className="w-full sm:w-auto"
            >
                Réinitialiser Filtres
            </Button>
            <Button 
              type="primary"
              icon={<PrinterOutlined />}
              onClick={openPrintModal}
              className="w-full sm:w-auto"
            >
                Imprimer la liste
            </Button>
          </div>
        </div>

        {selectedOrderIds.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-blue-200 bg-blue-50 p-3">
            <div>
              <Text className="block">{selectedOrderIds.length} commande(s) sélectionnée(s)</Text>
              <Text strong className="text-green-700">
                Montant à recevoir : {selectedAmountToReceive.toLocaleString('fr-FR')} {getDeviseActive()}
                <Tooltip title="Ouvrir la calculatrice avec ce montant">
                  <Button
                    type="text"
                    size="small"
                    icon={<CalculatorOutlined />}
                    onClick={showCalculator}
                    aria-label="Ouvrir la calculatrice"
                  />
                </Tooltip>
              </Text>
            </div>
            <Space wrap>
              <Button icon={<UserOutlined />} onClick={showBulkAssignmentModal} disabled={!hasSelectedOrdersAssignable}>
                Attribuer un livreur
              </Button>
              <Button type="primary" icon={<DollarOutlined />} onClick={showBulkPaymentModal} disabled={!hasSelectedOrdersPayable}>
                Marquer comme payées
              </Button>
              <Button onClick={showBulkRescheduleModal} disabled={!hasSelectedOrdersCancellable}>
                Reporter la livraison
              </Button>
              <Button danger icon={<StopOutlined />} onClick={showBulkCancelModal} disabled={!hasSelectedOrdersCancellable}>
                Annuler les commandes sélectionnées
              </Button>
            </Space>
          </div>
        )}

        {/* Affichage du client sélectionné */}
        {selectedCustomerId && selectedCustomerName && (
          <div style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: 'var(--private-soft, #f0f9ff)',
            border: `1px solid ${PRIMARY_COLOR}`, 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <UserOutlined style={{ color: PRIMARY_COLOR, marginRight: '8px' }} />
              <span style={{ fontWeight: 'bold', color: '#0c4a6e' }}>
                Client sélectionné : {selectedCustomerName}
              </span>
            </div>
            <Button 
              type="text" 
              size="small"
              onClick={() => {
                setSelectedCustomerId(null);
                setSelectedCustomerName('');
                setSearchText('');
                setClientSearchOptions([]);
              }}
              style={{ color: PRIMARY_COLOR }}
            >
              ✕ Supprimer le filtre
            </Button>
          </div>
        )}

        {/* Affichage des tags sélectionnés */}
        {selectedTags && selectedTags.length > 0 && (
          <div style={{
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: 'var(--private-soft, #f0f2ff)',
            border: '1px solid var(--private-ink, #6366f1)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold', color: 'var(--private-ink, #4338ca)', marginRight: '8px' }}>
                Tags sélectionnés :
              </span>
              {selectedTags.map((tag, index) => (
                <Tag key={index} color="purple" style={{ margin: '2px' }}>
                  {tag}
                </Tag>
              ))}
            </div>
            <Button
              type="text"
              size="small"
              onClick={() => setSelectedTags([])}
              style={{ color: 'var(--private-ink, #6366f1)' }}
            >
              ✕ Supprimer les tags
            </Button>
          </div>
        )}

        {/* Affichage du filtre par variante */}
        {selectedVariantId && (
          <div style={{
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tag color="orange" style={{ marginRight: '8px' }}>Variante</Tag>
              <span style={{ fontWeight: 'bold', color: '#92400e' }}>
                {selectedVariantName || 'Variante'}
                {selectedProductName && ` (${selectedProductName})`}
              </span>
            </div>
            <Button
              type="text"
              size="small"
              onClick={() => {
                // Réinitialiser le filtre de variante et recharger
                setSelectedVariantId(null);
                setSelectedVariantName('');
                setSelectedProductName('');
                // Supprimer les query params de variante de l'URL
                const params = new URLSearchParams(location.search);
                params.delete('variantId');
                params.delete('variantName');
                params.delete('productName');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                // Recharger les commandes sans le filtre de variante
                loadOrders(selectedDeliveryPersonId, selectedDate, selectedStatus, selectedCustomerId, selectedTags, selectedDeliveryDate, null, 1, pagination.pageSize);
              }}
              style={{ color: '#f59e0b' }}
            >
              ✕ Supprimer le filtre
            </Button>
          </div>
        )}

        {isMobile ? (
          /* Mobile : une carte par commande, mêmes colonnes que le tableau (sans lignes dépliables) */
          <ColumnsCardList
            columns={columns}
            // Même tri par défaut que le tableau : plus récentes d'abord
            dataSource={[...filteredOrders].sort((x, y) => new Date(y.created_at || 0) - new Date(x.created_at || 0))}
            loading={loading}
            emptyText="Aucune commande"
            rowSelection={{
              selectedRowKeys: selectedOrderIds,
              onChange: setSelectedOrderIds,
              getCheckboxProps: (order) => ({
                disabled: !canSelectOrderForBulkAction(order)
              })
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              // Total synthétique : garde le bouton "suivant" tant que l'API signale une suite
              total: pagination.hasMore
                ? (pagination.current * pagination.pageSize) + 1
                : filteredOrders.length,
              onChange: handlePaginationChange
            }}
            onCardClick={(record) => {
              // Conserver les filtres actuels dans l'URL de destination
              const currentParams = new URLSearchParams(location.search);
              navigate(`/app/orders/${record.id}?returnFilters=${encodeURIComponent(currentParams.toString())}`);
            }}
          />
        ) : (
        <Table
          columns={columns}
          dataSource={filteredOrders}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedOrderIds,
            onChange: setSelectedOrderIds,
            getCheckboxProps: (order) => ({
              disabled: !canSelectOrderForBulkAction(order)
            })
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            // Le total synthétique maintient le bouton "suivant" tant que l'API signale une suite.
            total: pagination.hasMore
              ? (pagination.current * pagination.pageSize) + 1
              : filteredOrders.length,
            pageSizeOptions: ['10', '15', '20', '50'],
            showSizeChanger: window.innerWidth > 768, // Masquer les options de taille sur mobile
            showQuickJumper: window.innerWidth > 768, // Masquer le saut rapide sur mobile
            showTotal: (total, range) => {
              const displayedCount = `${range[0]}-${range[1]}`;

              return pagination.hasMore
                ? `${displayedCount} commandes — d'autres commandes sont disponibles`
                : `${displayedCount} sur ${total} commandes`;
            },
            onChange: handlePaginationChange
          }}
          scroll={{
            // Le défilement reste disponible sur les petits écrans, mais il est
            // retiré sur desktop pour utiliser toute la largeur de la page.
            x: window.innerWidth < 1280 ? 'max-content' : undefined,
            scrollToFirstRowOnChange: true 
          }}
          size={window.innerWidth < 768 ? 'small' : 'middle'}
          expandable={{
            expandedRowRender,
            rowExpandable: record => true,
            onExpand: handleExpand,
          }}
          onRow={(record) => ({
            onClick: () => {
              // Conserver les filtres actuels dans l'URL de destination
              const currentParams = new URLSearchParams(location.search);
              navigate(`/app/orders/${record.id}?returnFilters=${encodeURIComponent(currentParams.toString())}`);
            },
            style: { cursor: 'pointer' },
            title: 'Cliquer pour voir les détails de la commande'
          })}
        />
        )}

        {/* La calculatrice travaille uniquement sur une expression locale sans modifier la sélection. */}
        <Modal
          title="Calculatrice"
          open={isCalculatorVisible}
          onCancel={() => setIsCalculatorVisible(false)}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 360}
        >
          <Input
            value={calculatorExpression}
            onChange={(event) => {
              setCalculatorExpression(event.target.value);
              setCalculatorResult(null);
            }}
            inputMode="decimal"
            suffix={getDeviseActive()}
            className="mb-3 text-right"
            aria-label="Expression de calcul"
          />
          <div className="mb-3 min-h-8 rounded bg-gray-50 p-2 text-right text-lg font-semibold">
            {calculatorResult === null
              ? '—'
              : calculatorResult === 'Erreur'
                ? 'Expression invalide'
                : `${Number(calculatorResult).toLocaleString('fr-FR')} ${getDeviseActive()}`}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              '7', '8', '9', '/',
              '4', '5', '6', '*',
              '1', '2', '3', '-',
              'clear', '0', 'backspace', '+',
              '(', ')', '.', '='
            ].map((key) => (
              <Button
                key={key}
                type={key === '=' ? 'primary' : 'default'}
                danger={key === 'clear'}
                onClick={() => handleCalculatorKey(key)}
              >
                {key === 'clear' ? 'C' : key === 'backspace' ? '⌫' : key}
              </Button>
            ))}
          </div>
        </Modal>

        {/* Le modal réutilise les champs métier de la fiche commande pour l'attribution groupée. */}
        <Modal
          title="Attribuer un livreur aux commandes sélectionnées"
          open={isBulkAssignmentModalVisible}
          onCancel={() => setIsBulkAssignmentModalVisible(false)}
          onOk={handleBulkAssignOrders}
          okText="Attribuer"
          cancelText="Annuler"
          confirmLoading={bulkAssignmentLoading}
          width={window.innerWidth < 768 ? '95vw' : 600}
          style={window.innerWidth < 768 ? { top: 20 } : {}}
        >
          <Form form={bulkAssignmentForm} layout="vertical">
            <Form.Item
              label="Livreur"
              name="delivery_person_id"
              rules={[{ required: true, message: 'Veuillez sélectionner un livreur' }]}
            >
              {/* Recherche nom/téléphone et liste tactile contenue dans le modal sur mobile. */}
              <Select
                {...getDeliveryPersonSelectProps(isMobile)}
                placeholder="Sélectionner un livreur"
                loading={loadingDeliveryPersons}
                showSearch
                optionFilterProp="searchText"
              >
                {deliveryPersons.map((person) => (
                  <Select.Option key={person.id} value={person.id} searchText={`${person.name || ''} ${person.phone || ''}`}>
                    {person.name}{person.phone ? ` (${person.phone})` : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Date de livraison"
              name="delivery_date"
              tooltip="Laissez vide pour utiliser la date par défaut"
            >
              <DatePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder="Sélectionner une date (optionnel)"
                defaultPickerValue={dayjs()}
                disabledDate={(current) => {
                  if (!current) return true;

                  // Restreint le calendrier à la même plage opérationnelle que l'attribution depuis la fiche commande.
                  const currentDay = dayjs(current);
                  const today = dayjs().startOf('day');
                  const maxDate = dayjs().add(7, 'days').endOf('day');
                  return !currentDay.isBetween(today, maxDate, 'day', '[]');
                }}
                showToday
                allowClear
              />
            </Form.Item>

            <Form.Item
              label="Frais de livraison"
              name="shipping_fee"
              tooltip="Frais de livraison à appliquer à chaque commande"
            >
              <Input type="number" min={0} step={500} addonAfter={getDeviseActive()} placeholder="Frais de livraison" />
            </Form.Item>

            <Form.Item
              label="Message au client"
              name="delivery_message_mode"
              tooltip="Choisissez l'envoi immédiat, la programmation habituelle à 09h00 ou l'absence de message"
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="scheduled">Programmer comme d’habitude</Radio>
                  <Radio value="immediate">Envoyer maintenant au client</Radio>
                  <Radio value="none">Ne pas envoyer de message au client</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Form>
        </Modal>

        {/* Le modal reprend les champs métier du détail pour l'annulation de plusieurs commandes. */}
        <Modal
          title="Annuler les commandes sélectionnées"
          open={isBulkCancelModalVisible}
          onCancel={() => setIsBulkCancelModalVisible(false)}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 500}
          style={window.innerWidth < 768 ? { top: 20 } : {}}
        >
          <div style={{ marginBottom: 16 }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            <Text strong>Attention !</Text>
            <Text style={{ display: 'block', marginTop: 8 }}>
              Cette action annulera définitivement {selectedOrderIds.length} commande(s). Chaque client sera automatiquement notifié par WhatsApp.
            </Text>
          </div>

          <Form form={bulkCancelForm} layout="vertical" onFinish={handleBulkCancelOrders}>
            <Form.Item name="reason" label="Motif d'annulation (optionnel)" rules={[{ required: false }]}>
              <Input.TextArea rows={4} placeholder="Expliquez pourquoi vous annulez ces commandes (optionnel)..." />
            </Form.Item>

            <Form.Item name="block_customer" valuePropName="checked" style={{ marginBottom: 16 }}>
              <Checkbox>
                <span style={{ fontWeight: 500 }}>Bloquer les clients concernés</span>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Chaque client concerné sera ajouté à la liste des clients non répondants.
                </div>
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setIsBulkCancelModalVisible(false)} icon={<CloseOutlined />}>
                  Fermer
                </Button>
                <Button type="primary" danger htmlType="submit" loading={bulkCancellationLoading} icon={<StopOutlined />}>
                  Confirmer l'annulation
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Le modal applique une méthode de paiement commune tout en gardant le montant propre à chaque commande. */}
        <Modal
          title="Marquer les commandes sélectionnées comme payées"
          open={isBulkPaymentModalVisible}
          onCancel={() => setIsBulkPaymentModalVisible(false)}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 500}
          style={window.innerWidth < 768 ? { top: 20 } : {}}
        >
          <Text style={{ display: 'block', marginBottom: 16 }}>
            Chaque commande sera encaissée avec son montant total et passera au statut terminé.
          </Text>

          <Form form={bulkPaymentForm} layout="vertical" onFinish={handleBulkMarkAsPaid}>
            <Form.Item
              name="method"
              label="Méthode de paiement"
              rules={[{ required: true, message: 'Veuillez sélectionner une méthode de paiement' }]}
            >
              <Select placeholder="Sélectionner une méthode de paiement">
                <Select.Option value="cash">Espèces</Select.Option>
                <Select.Option value="credit_card">Carte bancaire</Select.Option>
                <Select.Option value="bank_transfer">Virement bancaire</Select.Option>
                <Select.Option value="mobile_money">Mobile Money</Select.Option>
                <Select.Option value="check">Chèque</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="notes" label="Notes (optionnel)">
              <Input.TextArea rows={3} placeholder="Note ajoutée à chaque paiement" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setIsBulkPaymentModalVisible(false)} icon={<CloseOutlined />}>
                  Fermer
                </Button>
                <Button type="primary" htmlType="submit" loading={bulkPaymentLoading} icon={<DollarOutlined />}>
                  Confirmer le paiement
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Le report groupé ne modifie que la date de livraison des commandes actives sélectionnées. */}
        <Modal
          title="Reporter les livraisons sélectionnées"
          open={isBulkRescheduleModalVisible}
          onCancel={() => setIsBulkRescheduleModalVisible(false)}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 500}
          style={window.innerWidth < 768 ? { top: 20 } : {}}
        >
          <Text style={{ display: 'block', marginBottom: 16 }}>
            Choisissez la nouvelle date de livraison pour les commandes sélectionnées.
          </Text>

          <Form form={bulkRescheduleForm} layout="vertical" onFinish={handleBulkRescheduleOrders}>
            <Form.Item
              name="delivery_date"
              label="Nouvelle date de livraison"
              rules={[{ required: true, message: 'Veuillez sélectionner une nouvelle date de livraison' }]}
            >
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setIsBulkRescheduleModalVisible(false)} icon={<CloseOutlined />}>
                  Fermer
                </Button>
                <Button type="primary" htmlType="submit" loading={bulkRescheduleLoading}>
                  Confirmer le report
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal d'impression */}
        <Modal
          title="Imprimer la liste des commandes"
          open={printModalVisible}
          onOk={handlePrint}
          onCancel={closePrintModal}
          okText="Imprimer"
          cancelText="Annuler"
          confirmLoading={printLoading}
          okButtonProps={{ 
            icon: <PrinterOutlined />,
            disabled: printFilterType === 'by_date' && !printDeliveryDate
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Type de liste à imprimer :
              </label>
              <Radio.Group 
                value={printFilterType} 
                onChange={(e) => setPrintFilterType(e.target.value)}
                className="w-full"
              >
                <div className="space-y-2">
                  <Radio value="in_delivery">
                    Commandes en livraison
                  </Radio>
                  <Radio value="by_date">
                    Commandes prévues pour une date spécifique
                  </Radio>
                  <Radio value="current_filters">
                    Filtres actuels de la liste
                  </Radio>
                </div>
              </Radio.Group>
            </div>

            {printFilterType === 'by_date' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Date de livraison :
                </label>
                <DatePicker 
                  value={printDeliveryDate}
                  onChange={setPrintDeliveryDate}
                  format="DD/MM/YYYY"
                  placeholder="Sélectionner une date"
                  className="w-full"
                />
              </div>
            )}

            {printFilterType === 'current_filters' && (
              <div className="bg-blue-50 p-3 rounded border">
                <label className="block text-sm font-medium mb-2">
                  Filtres actuels appliqués :
                </label>
                <div className="text-sm text-gray-700">
                  {getCurrentFiltersDescription()}
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600">
              <p>
                La liste contiendra pour chaque commande :
              </p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Numéro de commande</li>
                <li>Nom du client</li>
                <li>Numéro de téléphone</li>
                <li>Lieu de livraison</li>
                <li>Prix total (en {getDeviseActive()})</li>
              </ul>
            </div>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default OrdersList;
